import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { alert, confirm } from "devextreme/ui/dialog";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { Button } from "devextreme-react/button";
import { LoadPanel, Popup } from "devextreme-react";
import Accordion from "devextreme-react/accordion";

import routes from "../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../common/Messages";
// The shared error-message ladder: axios errors with a response body, PascalCase or camelCase Message,
// string bodies, and the timeout case where error.response is undefined.
import { getApiErrorMessage } from "../../../common/concurrency";
import { useCommittedFilterRows } from "../../../common/reportFilters";
import { DisableEnableControls, loadLookupFromDataSource, loadLookupFromStaticDataSource } from "../../../common/Utils";
import FormField from "../../../dynamicFormBuilder/FormField";
import CommonReport from "../../../reports/components/CommonReport";
// The same two read-only report popups the full-commit screen uses. Reused, not reimplemented
// (Architecture.md R1); only the program they are keyed on differs.
import TransactionsGridPreview from "../genaric-component/TransactionsGridPreview";
import TransactionsPrintErrors from "../genaric-component/TransactionsPrintErrors";
import { QuestionsLayout } from "./Layout";
import "./preCommitTransaction.css";
import {
    ACCORDION_ANIMATION_MS,
    ACCORDION_PANEL,
    BUTTON,
    CREATE_TAX_LIABILITY_OPTIONS,
    DEFAULT_CREATE_TAX_LIABILITY,
    ERROR_REPORT_FORMAT,
    ERROR_REPORT_NAME,
    FIELD,
    LAYOUT_ID,
    LAYOUT_NAME,
    PAGE_BOTTOM_GAP_PX,
    PAGE_TITLE,
    PRECOMMIT_SECURITY_LOCKED,
    PREVIEW_TITLE,
    PRINT_ERRORS_TITLE,
    PROGRAM,
    REPORT_FULL_NAME,
    REPORT_NAME,
    REQUEST_TIMEOUT_MS,
} from "./constants";

const BUSY = Object.freeze({
    IDLE: "idle",
    PREVIEWING: "previewing",
    COMMITTING: "committing",
    LOADING_ERRORS: "loadingErrors",
});

/**
 * The questions panel's opening state, from FormCreate: cdsQuestionsTaxLiability := 'N' and
 * chkAccount.Checked := true — which is why the three account pickers start empty and disabled.
 *
 * A function rather than a module-level object so the state is seeded with a value nothing else holds
 * a reference to. Nothing calls it a second time: neither Reset nor the post-commit reset clears the
 * questions, exactly as neither Delphi handler does (see resetRange).
 */
const initialFormFields = () => ({
    [FIELD.CreateTaxLiability]: DEFAULT_CREATE_TAX_LIABILITY,
    [FIELD.AccruedReceivablesAccountRowId]: null,
    [FIELD.AccruedPayablesAccountRowId]: null,
    [FIELD.SalesAccountRowId]: null,
    [FIELD.UsePredefinedAccounts]: true,
    // Controlled like the rest, so the TextArea is never an uncontrolled input. It is deliberately
    // absent from buildRequest: SPOM_COMMIT has no parameter to carry it and the DTO has no member for
    // it, so the memo is display-only - see Layout.js, group 4.
    [FIELD.Comment]: "",
});

/*
 * Hooks .op-preview-popup onto the Preview popup's .dx-popup-content, which is the only way to reach
 * it: DevExtreme renders a Popup into an overlay outside this component's DOM subtree.
 *
 * Module-level rather than an inline object literal - devextreme-react compares props by identity, and
 * a fresh object each render makes it re-apply the option every time.
 */
const PREVIEW_POPUP_ATTR = { class: "op-preview-popup" };

/*
 * Print Errors gets its own class alongside the shared one. TransactionsPrintErrors renders a caption
 * block and the grid as two sibling .row divs, so the grid needs its row to become a flex item before
 * height="100%" has anything to resolve against - and that rule must not reach the eight other screens
 * mounting the same component, hence a popup-scoped hook rather than a change to the component.
 */
const ERRORS_POPUP_ATTR = { class: "op-preview-popup precommit-errors-popup" };

const isBlank = (value) => value === null || value === undefined || value === "";

/**
 * "Did the user enter any filter criteria?", answered the way CommonReport answers it for itself in
 * getRangeFilters: a row counts only when `from` is set.
 *
 * An orderBy-only row deliberately does NOT count. It sorts the preview but contributes no predicate,
 * and the Delphi test is on wbGetRange.Where, which is predicates only.
 */
const hasFilterCriteria = (rows) => (rows || []).some((row) => row && !isBlank(row.from));

/**
 * OM "Pre-Commit Transactions" (legacy OMD0025).
 *
 * AC Pre-Commit runs SPOM_COMMIT server-side with @inPreCommitFlag='P' over the transactions matching
 * the where-builder filter, and reports the batch number and record count. Preview runs the same
 * filter through a read-only SELECT; Print Errors reads back whatever the run logged. All three are
 * keyed on PROGRAM ("OMWEBPRECO"), so this screen's error log is its own and neither clears nor
 * displays the full-commit screen's (approved decision 1).
 *
 * This is an OPERATION screen: no CustomStore, no <Editing>, no per-row read-modify-write and no
 * record grid of its own, so Architecture.md rules 4-6, 8, 10, 12 and 14-16 (including optimistic
 * concurrency) do not apply. There is nothing here to Add or Edit.
 *
 * The legacy Exit button is dropped: it closed the Delphi MDI child, and the web equivalent is
 * navigating away. The legacy Comment memo is kept for visual parity but is display-only - uOMD0025.pas
 * never reads edtComment and SPOM_COMMIT has no parameter for it, so it is not submitted.
 */
const PreCommitTransaction = (props) => {
    const accessToken = props.oidc?.user?.access_token;
    const currentUser = props.oidc?.user;
    const dynamicFormSetting = props.oidc?.user?.profile?.DYNAMICFORMSETTING;

    const formRefs = useRef({});
    const reportRef = useRef(null);
    const pageRef = useRef(null);
    const accordionRef = useRef(null);

    const [formFields, setFormFields] = useState(initialFormFields);
    const [filterWhereInfoList, setFilterWhereInfoList] = useState([]);
    const [busy, setBusy] = useState(BUSY.IDLE);

    /**
     * OMSETUP.PRECOMMITSECURITY for the signed-in company. 'N' locks "Use Predefined Acc." checked, so
     * the account overrides are unreachable. Held as the raw code rather than a boolean so the
     * comparison stays the one FormCreate makes, including its "anything but 'N' allows it" fallthrough
     * for a blank or missing setup row.
     */
    const [preCommitSecurity, setPreCommitSecurity] = useState(null);

    // One object rather than three useStates: React 17 does not batch setState across an await, so
    // separate updates after a fetch each cost an extra render of the whole page.
    // The accordion starts EXPANDED (the one panel selected): the questions it holds are the screen's
    // actual input, unlike the sibling commit screen whose panel holds only a comment box.
    const [selectedPanels, setSelectedPanels] = useState(ACCORDION_PANEL);

    /*
     * --op-chrome is by definition everything above .op-filter, which is exactly the filter's own
     * distance from the top of the viewport - so measure it rather than maintain a pixel guess.
     *
     * The guessed constants this replaces were wrong in both accordion states (too large expanded, so
     * the filter fell short and left a dead band under the grid) and would have gone stale again on any
     * theme, zoom or breadcrumb change. Measuring also removes the need to know the accordion's height
     * at all: the ResizeObserver below fires throughout the expand/collapse animation, so the filter
     * tracks it continuously instead of snapping between two hardcoded values.
     *
     * The CSS keeps its values as the pre-measurement fallback for the first paint and for any browser
     * without ResizeObserver; the inline property set here wins over them.
     */
    useLayoutEffect(() => {
        const page = pageRef.current;
        if (!page) {
            return undefined;
        }

        const applyChrome = () => {
            const top = page.getBoundingClientRect().top;
            page.style.setProperty("--op-chrome", `${Math.max(0, Math.round(top)) + PAGE_BOTTOM_GAP_PX}px`);
        };

        applyChrome();

        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(applyChrome);
        if (observer && accordionRef.current) {
            observer.observe(accordionRef.current);
        }
        window.addEventListener("resize", applyChrome);

        return () => {
            if (observer) {
                observer.disconnect();
            }
            window.removeEventListener("resize", applyChrome);
        };
    }, []);

    const [preview, setPreview] = useState({ rows: [], visible: false });
    const [errorLog, setErrorLog] = useState({ rows: [], visible: false });

    const isBusy = busy !== BUSY.IDLE;
    const accountsLocked = formFields[FIELD.UsePredefinedAccounts] === true;
    const securityLocked = preCommitSecurity === PRECOMMIT_SECURITY_LOCKED;

    /**
     * Reports an outcome through DevExtreme's own `alert` dialog - the same module this screen already
     * takes `confirm` from - rather than a `notify` toast.
     *
     * Modal is the faithful reading of the legacy: uOMD0025.pas raises every one of these through
     * ab_messagedlg, which blocks until acknowledged. A toast auto-dismisses after five seconds, so a
     * user who looked away missed the batch number a pre-commit run exists to report, and missed the
     * two refusals in confirmPreCommit entirely. `alert` also renders its message as HTML, so the
     * <br/> separators the server puts between batch number and record count survive.
     *
     * Every caller drops the LoadPanel BEFORE awaiting this, never in a `finally` after it, so the
     * shading is gone by the time the dialog paints instead of the two overlays sharing the screen.
     *
     * The title carries the severity the toast colour used to: MESSAGES.SUCCESS for a run that landed,
     * MESSAGES.ERROR for everything else, which is why it is the default.
     */
    const showMessage = useCallback((message, title = MESSAGES.ERROR) => alert(message, title), []);

    const requestConfig = useCallback(
        () => ({
            headers: {
                "Content-Type": "application/json",
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
            },
            timeout: REQUEST_TIMEOUT_MS,
        }),
        [accessToken]
    );

    /**
     * CommonReport fires this once, from loadReport's .then. It stays correct because the editable
     * filter grid is bound to an ArrayStore wrapping this same array instance, which cell edits mutate
     * in place - so the reference captured here stays live. Do not clone it per render.
     */
    const handleReportStateChange = useCallback((reportState) => {
        setFilterWhereInfoList(reportState?.filterWhereInfoList ?? []);
    }, []);

    /**
     * FormCreate's OMSETUP read. Reuses the OM Setup screen's own endpoint rather than adding a
     * narrower one (Architecture.md R1): GetSetupDataByRowId ignores its rowid argument and resolves
     * the row from the signed-in company, which is exactly the lookup this screen needs.
     *
     * A failure leaves preCommitSecurity null, i.e. NOT locked - the same direction FormCreate falls in
     * when ab_ReturnValueFromSQL comes back null.
     */
    useEffect(() => {
        let cancelled = false;

        axios
            .get(`${window._env_.REACT_APP_APIS}/${routes.OM.GetOMSetup}`, requestConfig())
            .then((response) => {
                if (!cancelled) {
                    setPreCommitSecurity(response?.data?.PRECOMMITSECURITY ?? null);
                }
            })
            .catch(() => {
                // Deliberately silent: the flag only decides whether ONE checkbox is togglable, and a
                // toast about a setup read the user did not ask for is noise. The server re-checks
                // nothing here because PRECOMMITSECURITY gates UI reach, not data integrity.
            });

        return () => {
            cancelled = true;
        };
    }, [requestConfig]);

    /**
     * chkAccountClick, exactly: checked greys the three pickers, unchecked enables them.
     * DisableEnableControls takes `disabled`, so the flag passes straight through.
     *
     * The checkbox's own enabled state is the separate PRECOMMITSECURITY gate.
     */
    const applyAccountRules = useCallback((usePredefinedAccounts, locked) => {
        DisableEnableControls(FIELD.AccruedReceivablesAccountRowId, usePredefinedAccounts, formRefs);
        DisableEnableControls(FIELD.AccruedPayablesAccountRowId, usePredefinedAccounts, formRefs);
        DisableEnableControls(FIELD.SalesAccountRowId, usePredefinedAccounts, formRefs);
        DisableEnableControls(FIELD.UsePredefinedAccounts, locked, formRefs);
    }, []);

    const handleFieldChange = useCallback(
        (name, value) => {
            if (name === FIELD.UsePredefinedAccounts) {
                // Runs for programmatic changes too, deliberately: after Reset the checkbox returns to
                // checked and the enable/disable state has to follow it.
                applyAccountRules(value === true, securityLocked);
            }
            // The four value fields are absent on purpose: nothing derives from them, and this handler
            // must never react to a value it wrote itself.
        },
        [applyAccountRules, securityLocked]
    );

    const handleFormLoad = useCallback(() => {
        // FormField calls onFormLoad from fetchData's `finally`, BEFORE React has committed the child
        // Fields, so formRefs is still empty at that instant. Same 100ms shape as the sibling
        // operation screens.
        setTimeout(() => {
            // Mandatory, not an optimisation: Fields.js renders a `select` with NO dataSource at all
            // (Architecture.md rule 3 - load the options via formRefs in onFormLoad).
            loadLookupFromStaticDataSource(FIELD.CreateTaxLiability, CREATE_TAX_LIABILITY_OPTIONS, formFields, formRefs);

            // The shared AC Account lookup, which already reproduces uACM0001's qryACAccount filter
            // (Type='D', active company/module/recordtype) AND its current-earnings exclusion. All
            // three pickers read the same source, as all three TDBArgosLookupEdits do in the DFM.
            const accountsUrl = `${window._env_.REACT_APP_APIS}/${routes.AC.GetAcAccounts}`;
            loadLookupFromDataSource(accessToken, formFields, formRefs, FIELD.AccruedReceivablesAccountRowId, accountsUrl);
            loadLookupFromDataSource(accessToken, formFields, formRefs, FIELD.AccruedPayablesAccountRowId, accountsUrl);
            loadLookupFromDataSource(accessToken, formFields, formRefs, FIELD.SalesAccountRowId, accountsUrl);

            applyAccountRules(formFields[FIELD.UsePredefinedAccounts] === true, securityLocked);
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Belt-and-braces re-apply. onFormLoad fires once per FormField mount and the dynamic-form editor's
     * save/discard path remounts the fields without re-firing it - and PRECOMMITSECURITY arrives from
     * its own request, which may well land after that first pass.
     */
    useEffect(() => {
        applyAccountRules(accountsLocked, securityLocked);
    }, [accountsLocked, securityLocked, applyAccountRules]);

    // Commits any in-progress filter cell before reading the rows - see common/reportFilters.js for why
    // the onReportStateChange array is the wrong source at submit time.
    const readFilterRows = useCommittedFilterRows(reportRef, filterWhereInfoList);

    /**
     * One payload shape for Preview and AC Pre-Commit, so the two cannot disagree about the row set.
     *
     * Each endpoint binds only what its own DTO declares and ignores the rest: the Preview endpoint
     * (UpdateOrderStageRequestDto) reads FilterWhereInfoList and Program, the commit endpoint
     * (PreCommitTransactionRequestDTO) reads FilterWhereInfoList and the five question fields. Program
     * is deliberately NOT on the commit DTO - the program value is pinned server-side, so the client
     * cannot aim a run at another screen's error bucket.
     */
    const buildRequest = useCallback(
        (rows) => ({
            FilterWhereInfoList: rows || [],
            Program: PROGRAM,
            CreateTaxLiability: formFields[FIELD.CreateTaxLiability],
            UsePredefinedAccounts: formFields[FIELD.UsePredefinedAccounts] === true,
            AccruedReceivablesAccountRowId: formFields[FIELD.AccruedReceivablesAccountRowId] || null,
            AccruedPayablesAccountRowId: formFields[FIELD.AccruedPayablesAccountRowId] || null,
            SalesAccountRowId: formFields[FIELD.SalesAccountRowId] || null,
        }),
        [formFields]
    );

    // Stable identities: an inline arrow would change on every render and, via the Popups, keep the
    // memoised grid subtrees re-rendering.
    const hidePreview = useCallback(() => setPreview((previous) => ({ ...previous, visible: false })), []);
    const hideErrorLog = useCallback(() => setErrorLog((previous) => ({ ...previous, visible: false })), []);

    /**
     * wbGetRange.Reset — the RANGE only.
     *
     * The questions panel is deliberately NOT cleared. Both Delphi call sites reset just the
     * where-builder: btnResetClick is a one-liner, and btnCommitClick's last statement is the same
     * single call. Wiping the tax-liability flag and the three account picks here would be a behaviour
     * this screen never had, and it would make the user re-pick two required accounts after every run.
     *
     * Used by the Reset button AND, unconditionally, after every commit attempt — success or failure
     * alike, because that reset sits at the end of btnCommitClick's else branch rather than inside its
     * success arm.
     */
    const resetRange = useCallback(() => {
        setPreview({ rows: [], visible: false });
        // Re-fetches the field definitions, rebuilds the filter store and re-fires onReportStateChange
        // with a fresh array. It does NOT remount FormField, so onFormLoad will not fire again - which
        // is correct: the imperatively-loaded SelectBox dataSources survive, and the enable/disable rule
        // is re-applied by the useEffect above.
        if (reportRef.current && typeof reportRef.current.loadReport === "function") {
            reportRef.current.loadReport();
        }
    }, []);

    const handlePreview = useCallback(async () => {
        // No confirmations: the Delphi Preview goes straight to wbGetRange.Preview and writes nothing.
        const filterRows = await readFilterRows();

        setBusy(BUSY.PREVIEWING);
        // Held rather than raised where it is detected, so the dialog opens after `finally` has taken
        // the LoadPanel down - see showMessage.
        let failure = null;
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.OM.GetTransHeader}`,
                JSON.stringify(buildRequest(filterRows)),
                requestConfig()
            );

            // This endpoint returns the row array directly rather than a ServiceResponse envelope, so a
            // non-array is the only signal that something went wrong. An empty array IS legitimate -
            // the shared grid says so through its own no-data text.
            if (Array.isArray(response?.data)) {
                setPreview({ rows: response.data, visible: true });
            } else {
                setPreview({ rows: [], visible: false });
                failure = MESSAGES.ERROR;
            }
        } catch (error) {
            setPreview({ rows: [], visible: false });
            failure = getApiErrorMessage(error, MESSAGES.ERROR);
        } finally {
            setBusy(BUSY.IDLE);
        }

        if (failure) {
            await showMessage(failure);
        }
    }, [readFilterRows, buildRequest, requestConfig, showMessage]);

    const handlePrintErrors = useCallback(async () => {
        setBusy(BUSY.LOADING_ERRORS);
        let failure = null;
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.OM.GetABErrorsHeaders}`,
                JSON.stringify(PROGRAM),
                requestConfig()
            );

            if (Array.isArray(response?.data)) {
                setErrorLog({ rows: response.data, visible: true });
            } else {
                setErrorLog({ rows: [], visible: false });
                failure = MESSAGES.ERROR;
            }
        } catch (error) {
            setErrorLog({ rows: [], visible: false });
            failure = getApiErrorMessage(error, MESSAGES.ERROR);
        } finally {
            setBusy(BUSY.IDLE);
        }

        if (failure) {
            await showMessage(failure);
        }
    }, [requestConfig, showMessage]);

    /**
     * btnCommitClick's guard clauses, in the Delphi's order.
     *
     * 1. The no-filter question, asked against the COMMITTED rows so a value typed without tabbing out
     *    does not look like an empty filter. A No exits without running, as `exit` does.
     * 2. The two required-account refusals, which apply ONLY while "Use Predefined Acc." is unchecked.
     *    Receivables is tested first and the function returns on the first failure, so exactly one
     *    message appears - the legacy shows them through separate ab_messagedlg calls in an if/else-if
     *    chain, never both at once.
     *
     * SALES ACCOUNT IS NOT CHECKED, and must not be: the Delphi chain has no third branch (approved
     * decision 3). The server agrees, and it is the authority - these two are mirrored here only so an
     * unusable form costs no round trip.
     *
     * Returns true when the run may proceed.
     */
    const confirmPreCommit = useCallback(
        async (rows) => {
            if (!hasFilterCriteria(rows) && (await confirm(MESSAGES.PRECOMMIT_NO_FILTER_CONFIRM, PAGE_TITLE)) !== true) {
                return false;
            }

            if (formFields[FIELD.UsePredefinedAccounts] !== true) {
                if (isBlank(formFields[FIELD.AccruedReceivablesAccountRowId])) {
                    await showMessage(MESSAGES.PRECOMMIT_ACCRUED_RECEIVABLES_REQUIRED);
                    return false;
                }

                if (isBlank(formFields[FIELD.AccruedPayablesAccountRowId])) {
                    await showMessage(MESSAGES.PRECOMMIT_ACCRUED_PAYABLES_REQUIRED);
                    return false;
                }
            }

            return true;
        },
        [formFields, showMessage]
    );

    /*
        DevExtreme reports the delta (addedItems / removedItems), not the resulting selection, so the
        panel list is rebuilt from both rather than assigned from one. collapsible is on and multiple
        is off, so this settles on either the single panel or nothing.
    */
    const handlePanelSelectionChanged = useCallback((e) => {
        setSelectedPanels((current) => {
            const next = current.filter((panel) => !e.removedItems.includes(panel));
            return e.addedItems.length ? [...next, ...e.addedItems] : next;
        });
    }, []);

    // Drives the collapsed-state class on the filter wrapper below, so --op-chrome can give the grid
    // back the height the questions panel was using.
    const isQuestionsPanelOpen = selectedPanels.length > 0;

    const handlePreCommit = useCallback(async () => {
        // Read BEFORE the confirmation, not after: question 1 asks whether to run with no filter at
        // all, so it has to see the committed rows or it fires on a filter the user did type.
        const filterRows = await readFilterRows();

        if (!(await confirmPreCommit(filterRows))) {
            return;
        }

        setBusy(BUSY.COMMITTING);
        // Reported only after `finally` has reset the screen, so the dialog the user has to dismiss is
        // the last thing that moves rather than something resetRange repaints underneath.
        let outcome;
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.OM.PreCommitTransactions}`,
                JSON.stringify(buildRequest(filterRows)),
                requestConfig()
            );

            const payload = response?.data;
            outcome = {
                message: payload?.message || MESSAGES.PRECOMMIT_FAILED,
                title: payload?.success ? MESSAGES.SUCCESS : MESSAGES.ERROR,
            };
        } catch (error) {
            outcome = { message: getApiErrorMessage(error, MESSAGES.PRECOMMIT_FAILED), title: MESSAGES.ERROR };
        } finally {
            setBusy(BUSY.IDLE);
            // wbGetRange.Reset runs unconditionally at the end of btnCommitClick - after a successful
            // run AND after a failed one - so it lives in `finally`, not in the success branch.
            resetRange();
        }

        await showMessage(outcome.message, outcome.title);
    }, [readFilterRows, confirmPreCommit, buildRequest, requestConfig, resetRange, showMessage]);

    return (
        <>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                visible={isBusy}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
                message={
                    busy === BUSY.COMMITTING
                        ? MESSAGES.PRECOMMITTING_TRANSACTIONS
                        : busy === BUSY.LOADING_ERRORS
                            ? MESSAGES.PRECOMMIT_LOADING_ERRORS
                            : MESSAGES.PREVIEWING_PRECOMMIT_TRANSACTIONS
                }
            />

            {/*
                The page title and the four actions sit in the accordion TITLE bar, and the legacy
                grpQuestions panel in its BODY - the arrangement CommitTransaction.js uses, so the two
                commit screens read alike. Collapsing it hands the viewport back to the Record Range
                filter without taking the buttons away, which is the point of the control here: the
                questions are set once per run, the filter is what gets adjusted repeatedly.
            */}
            <div className="row">
                <div className="col-md-12 main-page">
                    <div id="accordion" ref={accordionRef}>
                        <Accordion
                            dataSource={ACCORDION_PANEL}
                            collapsible={true}
                            multiple={false}
                            animationDuration={ACCORDION_ANIMATION_MS}
                            selectedItems={selectedPanels}
                            onSelectionChanged={handlePanelSelectionChanged}
                            id="accordion-container"
                            className="accordion-height precommit-accordion"
                            itemTitleRender={() => (
                                <Toolbar className="accordian-toolbar">
                                    <Item location="before">
                                        <div className="title">
                                            <h6 style={{ color: window._env_.Color2 }}>{PAGE_TITLE}</h6>
                                        </div>
                                    </Item>

                                    <Item location="after">
                                        <Button className="accordianButton" text={BUTTON.PreCommit} type="default" onClick={handlePreCommit} disabled={isBusy} />
                                    </Item>
                                    <Item location="after">
                                        <Button className="accordianButton" text={BUTTON.Preview} onClick={handlePreview} disabled={isBusy} />
                                    </Item>
                                    <Item location="after">
                                        <Button className="accordianButton" text={BUTTON.Reset} onClick={resetRange} disabled={isBusy} />
                                    </Item>
                                    <Item location="after">
                                        <Button className="accordianButton" text={BUTTON.PrintErrors} onClick={handlePrintErrors} disabled={isBusy} />
                                    </Item>
                                </Toolbar>
                            )}
                            itemRender={() => (
                                /*
                                    hasOnlyOneColumn is deliberately NOT passed. Layout.js declares four
                                    groups, and it is FormField's default col-md-4 flex that lays them
                                    out side by side; passing it would collapse them back into one
                                    narrow single-column stack.
                                */
                                <div className="precommit-parameters">
                                    <FormField
                                        currrentUser={currentUser}
                                        layoutId={LAYOUT_ID}
                                        layoutName={LAYOUT_NAME}
                                        defaultFormSchema={QuestionsLayout}
                                        formFields={formFields}
                                        setformFields={setFormFields}
                                        handleFieldValueChange={handleFieldChange}
                                        DYNAMICFORMSETTING={dynamicFormSetting}
                                        formRefs={formRefs}
                                        onFormLoad={handleFormLoad}
                                    />
                                </div>
                            )}
                        />
                    </div>
                </div>
            </div>

            {/*
                The filter gets the rest of the page. --op-chrome has to shrink when the accordion is
                collapsed, or the filter grid keeps reserving the height the questions panel no longer
                occupies.
            */}
            <div ref={pageRef} className={isQuestionsPanelOpen ? "precommit-page" : "precommit-page precommit-page-collapsed"}>
                <div className="op-filter">
                    <CommonReport
                        name={REPORT_NAME}
                        fullName={REPORT_FULL_NAME}
                        onReportStateChange={handleReportStateChange}
                        innerRef={reportRef}
                    />
                </div>
            </div>

            {preview.visible && (
                <Popup
                    visible={preview.visible}
                    onHiding={hidePreview}
                    dragEnabled={false}
                    hideOnOutsideClick={true}
                    showCloseButton={true}
                    showTitle={true}
                    title={PREVIEW_TITLE}
                    container=".dx-viewport"
                    width="94vw"
                    height="76vh"
                    wrapperAttr={PREVIEW_POPUP_ATTR}
                >
                    {/*
                        The grid FILLS the popup - see .op-preview-popup in the shared
                        operationScreen.css. Flexing rather than sizing the grid with a calc() that has
                        to guess the popup's own chrome: guessing high leaves the dead strip under the
                        rows that this screen had, guessing low clips the last row.

                        No ScrollView wrapper. An outer scroller gives the grid an unbounded height to
                        lay out against, so it renders at its content height inside a taller popup -
                        which is exactly the symptom it replaced.
                    */}
                    <div className="op-preview-body">
                        <div className="op-preview-grid">
                            <TransactionsGridPreview dataSource={preview.rows} height="100%" />
                        </div>
                    </div>
                </Popup>
            )}

            {errorLog.visible && (
                <Popup
                    visible={errorLog.visible}
                    onHiding={hideErrorLog}
                    dragEnabled={false}
                    hideOnOutsideClick={true}
                    showCloseButton={true}
                    showTitle={true}
                    title={PRINT_ERRORS_TITLE}
                    container=".dx-viewport"
                    width="94vw"
                    height="76vh"
                    wrapperAttr={ERRORS_POPUP_ATTR}
                >
                    {/*
                        Same flex treatment as Preview above. The report caption block is normal flow at
                        the top of .op-preview-body and the grid takes the rest, so the popup itself
                        never scrolls - the grid does, inside its own viewport.
                    */}
                    <div className="op-preview-body">
                        <div className="op-preview-grid">
                            <TransactionsPrintErrors
                                dataSource={errorLog.rows}
                                reportname={ERROR_REPORT_NAME}
                                reportformat={ERROR_REPORT_FORMAT}
                                height="100%"
                            />
                        </div>
                    </div>
                </Popup>
            )}
        </>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(PreCommitTransaction);
